import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getProducts,
  priceCart,
  getConfig,
  listOrders,
  getFavorites,
  addFavorite,
  removeFavorite,
  getCartItems,
  saveCartItems,
  getStockAlerts,
  toggleStockAlert,
  cancelOrder,
  ApiError,
  type PublicProduct,
  type PricedCart,
  type PublicOrder,
  type StorefrontConfig,
  type PublicUser,
  type CartItem,
} from "./api.ts";
import { showMainButton, showBackButton, haptic } from "./telegram.ts";
import { formatMoney } from "./format.ts";
import { handleImgError } from "./img.ts";
import { Stars } from "./Stars.tsx";
import { StampCard } from "./StampCard.tsx";
import { Checkout } from "./Checkout.tsx";
import { Wheel } from "./Wheel.tsx";
import { ProductSheet } from "./ProductSheet.tsx";
import { FilterSheet } from "./FilterSheet.tsx";
import { OrderTracker, isActiveOrder } from "./OrderTracker.tsx";
import { Account } from "./Account.tsx";

type View =
  | "catalog"
  | "cart"
  | "checkout"
  | "done"
  | "orders"
  | "track"
  | "account";
/** Cart keyed by product (or product::variant for variant lines) → quantity. */
type Cart = Record<string, number>;

/** Composite cart key for a product + optional variant. */
export function keyOf(productId: string, variantId?: string | null): string {
  return variantId ? `${productId}::${variantId}` : productId;
}
function parseKey(key: string): { productId: string; variantId: string | null } {
  const idx = key.indexOf("::");
  if (idx === -1) return { productId: key, variantId: null };
  return { productId: key.slice(0, idx), variantId: key.slice(idx + 2) };
}

const ALL = "all";

/**
 * Verified-user storefront: catalog → cart → checkout → confirmation. Cart
 * quantities live client-side for responsiveness, but every total is recomputed
 * server-side (POST /api/cart) and the order is created server-side
 * (POST /api/orders) — the client never trusts its own prices.
 */
export function Shop({
  user,
  onUserUpdate,
}: {
  user: PublicUser;
  onUserUpdate: (u: PublicUser) => void;
}) {
  const firstName = user.firstName;
  const [products, setProducts] = useState<PublicProduct[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cart, setCart] = useState<Cart>({});
  const [priced, setPriced] = useState<PricedCart | null>(null);
  const [view, setView] = useState<View>("catalog");
  const [order, setOrder] = useState<PublicOrder | null>(null);
  // Catalog browsing state (lifted so it survives cart/back navigation).
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>(ALL);
  const [brand, setBrand] = useState<string>(ALL);
  const [flavor, setFlavor] = useState<string>(ALL);
  // Nicotine strength (mg) as a string, or ALL. Stored as string to share the
  // single-select chip machinery with the other filter dimensions.
  const [strength, setStrength] = useState<string>(ALL);
  // Whether the filter bottom-sheet is open.
  const [filterOpen, setFilterOpen] = useState(false);
  // Product opened in the detail sheet (null = closed).
  const [detail, setDetail] = useState<PublicProduct | null>(null);
  const [config, setConfig] = useState<StorefrontConfig | null>(null);
  // Favorites (product ids) + a favorites-only view toggle.
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [favOnly, setFavOnly] = useState(false);
  // "Tell me when it's back" subscriptions, keyed like the cart.
  const [alerts, setAlerts] = useState<Set<string>>(new Set());
  // Guard: don't persist the cart until the durable cart has loaded, so the
  // initial empty state can't overwrite a saved cart on the server.
  const [cartLoaded, setCartLoaded] = useState(false);

  const loadProducts = () => {
    setLoadError(null);
    getProducts()
      .then(setProducts)
      .catch((e) =>
        setLoadError(e instanceof ApiError ? e.message : "failed to load"),
      );
  };

  useEffect(() => {
    loadProducts();
    getConfig()
      .then(setConfig)
      .catch(() => {
        /* nudges are optional; storefront works without config */
      });
    getFavorites()
      .then((ids) => setFavorites(new Set(ids)))
      .catch(() => {
        /* favorites are optional */
      });
    getStockAlerts()
      .then((rows) =>
        setAlerts(new Set(rows.map((a) => keyOf(a.productId, a.variantId)))),
      )
      .catch(() => {
        /* alerts are optional */
      });
    // Restore the durable cart (shared with the bot's /menu) so a webview
    // reload doesn't drop the basket.
    getCartItems()
      .then((items) => {
        const restored: Cart = {};
        for (const it of items)
          if (it.quantity > 0) restored[keyOf(it.productId, it.variantId)] = it.quantity;
        setCart(restored);
      })
      .catch(() => {
        /* durable cart is best-effort */
      })
      .finally(() => setCartLoaded(true));
  }, []);

  // Optimistic "notify me when back" toggle.
  const onToggleAlert = (productId: string, variantId?: string | null) => {
    const key = keyOf(productId, variantId);
    haptic("light");
    setAlerts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    void toggleStockAlert(productId, variantId).catch(() => {});
  };

  // Optimistic favorite toggle with server persistence.
  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        void removeFavorite(id).catch(() => {});
      } else {
        next.add(id);
        void addFavorite(id).catch(() => {});
      }
      return next;
    });
  };

  const items = useMemo<CartItem[]>(
    () =>
      Object.entries(cart)
        .filter(([, q]) => q > 0)
        .map(([key, quantity]) => ({ ...parseKey(key), quantity })),
    [cart],
  );
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  // Per-product totals across variants (cards, suggestions).
  const productTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const it of items) {
      totals.set(it.productId, (totals.get(it.productId) ?? 0) + it.quantity);
    }
    return totals;
  }, [items]);

  // Delivery availability + minimum-order gating (server also enforces both).
  const paused = !!config?.store.paused;
  const storeOpen = !config?.store || config.store.open;
  const preorderAvailable = !storeOpen && !paused && !!config?.store.preorderEnabled;
  const orderable = !paused && (storeOpen || preorderAvailable);
  const minOrderCents = config?.minOrderCents ?? 0;
  const belowMin =
    !!priced &&
    priced.subtotalCents > 0 &&
    minOrderCents > 0 &&
    priced.subtotalCents < minOrderCents;
  const canCheckout = !!priced?.valid && orderable && !belowMin;

  // Stamp-card summary for the catalog strip + account button context.
  const stampSummary = useMemo(() => {
    if (!config) return null;
    const size = config.stampCardSize;
    const count = Math.min(user.stampCount, size);
    return {
      count,
      size,
      freeVapeCredits: user.freeVapeCredits,
      remaining: Math.max(0, size - count),
    };
  }, [config, user.stampCount, user.freeVapeCredits]);

  // Redeemable free-vape reward for checkout: worth one cheapest eligible vape
  // in the cart, capped by the configured reward maximum.
  const freeVapeReward = useMemo(() => {
    if (!config || !priced || user.freeVapeCredits <= 0) return null;
    const eligibleCats = new Set(config.stampCategories);
    const catById = new Map((products ?? []).map((p) => [p.id, p.category]));
    const eligible = priced.lines.filter(
      (l) => !l.issue && eligibleCats.has(catById.get(l.productId) ?? ""),
    );
    if (eligible.length === 0) return null;
    const cheapest = Math.min(...eligible.map((l) => l.priceCents));
    return {
      credits: user.freeVapeCredits,
      discountCents: Math.min(cheapest, config.stampRewardMaxCents),
    };
  }, [config, priced, products, user.freeVapeCredits]);

  // Persist cart changes to the durable server cart (debounced) once loaded, so
  // the basket survives a webview reload and stays in sync with the bot's /menu.
  useEffect(() => {
    if (!cartLoaded) return;
    const timer = setTimeout(() => {
      void saveCartItems(items);
    }, 600);
    return () => clearTimeout(timer);
  }, [items, cartLoaded]);

  // Recompute totals server-side whenever the cart changes (debounced).
  useEffect(() => {
    if (items.length === 0) {
      setPriced(null);
      return;
    }
    const t = setTimeout(() => {
      priceCart(items)
        .then(setPriced)
        .catch(() => {
          /* transient; keep last known totals */
        });
    }, 250);
    return () => clearTimeout(t);
  }, [items]);

  // Native MainButton: contextual to catalog/cart. Checkout & confirmation use
  // in-page buttons to keep multi-step form control simple.
  useEffect(() => {
    if (view === "catalog") {
      if (itemCount === 0) return;
      const label = priced
        ? `View cart · ${formatMoney(priced.totalCents, priced.currency)}`
        : `View cart (${itemCount})`;
      return showMainButton(label, () => setView("cart"));
    }
    if (view === "cart" && priced && canCheckout) {
      return showMainButton(
        `Checkout · ${formatMoney(priced.totalCents, priced.currency)}`,
        () => setView("checkout"),
      );
    }
    return;
  }, [view, itemCount, priced, canCheckout]);

  // Native BackButton for the inner views.
  useEffect(() => {
    if (view === "cart") return showBackButton(() => setView("catalog"));
    if (view === "checkout") return showBackButton(() => setView("cart"));
    if (view === "done") return showBackButton(() => setView("catalog"));
    if (view === "orders") return showBackButton(() => setView("catalog"));
    if (view === "track") return showBackButton(() => setView("orders"));
    if (view === "account") return showBackButton(() => setView("catalog"));
    return;
  }, [view]);

  // Open the live tracker for a specific order.
  const trackOrder = (o: PublicOrder) => {
    setOrder(o);
    setView("track");
  };

  // Keyed by cart key (product id, or product::variant).
  const setQty = (key: string, quantity: number) =>
    setCart((prev) => {
      const current = prev[key] ?? 0;
      if (quantity > current) haptic("light");
      const next = { ...prev };
      if (quantity <= 0) delete next[key];
      else next[key] = quantity;
      return next;
    });

  // Cascading filter options: each dimension's choices are drawn only from the
  // products that already match the selections above it. Pick a category and the
  // brand/flavor/strength lists collapse to what that category actually carries;
  // pick a brand and flavor/strength narrow again, and so on.
  //
  // Category is the top of the cascade — always the full set.
  const categories = useMemo(() => {
    if (!products) return [];
    return [...new Set(products.map((p) => p.category))].sort();
  }, [products]);

  // Products passing the category selection (upstream of brand).
  const catFiltered = useMemo(
    () =>
      (products ?? []).filter((p) => category === ALL || p.category === category),
    [products, category],
  );
  const brands = useMemo(
    () =>
      [
        ...new Set(catFiltered.map((p) => p.brand).filter((b): b is string => !!b)),
      ].sort(),
    [catFiltered],
  );

  // …then the brand selection (upstream of flavor).
  const brandFiltered = useMemo(
    () => catFiltered.filter((p) => brand === ALL || p.brand === brand),
    [catFiltered, brand],
  );
  const flavors = useMemo(
    () =>
      [
        ...new Set(
          brandFiltered.map((p) => p.flavor).filter((f): f is string => !!f),
        ),
      ].sort(),
    [brandFiltered],
  );

  // …then the flavor selection (upstream of strength).
  const flavorFiltered = useMemo(
    () => brandFiltered.filter((p) => flavor === ALL || p.flavor === flavor),
    [brandFiltered, flavor],
  );
  const strengths = useMemo(
    () =>
      [
        ...new Set(
          flavorFiltered
            .map((p) => p.nicotineMg)
            .filter((n): n is number => n != null),
        ),
      ].sort((a, b) => a - b),
    [flavorFiltered],
  );

  // When a narrower upstream choice makes a downstream selection impossible
  // (e.g. switching category away from the one that had the picked brand),
  // silently drop the now-invalid selection so results never go empty by accident.
  useEffect(() => {
    if (brand !== ALL && !brands.includes(brand)) setBrand(ALL);
  }, [brands, brand]);
  useEffect(() => {
    if (flavor !== ALL && !flavors.includes(flavor)) setFlavor(ALL);
  }, [flavors, flavor]);
  useEffect(() => {
    if (strength !== ALL && !strengths.map(String).includes(strength))
      setStrength(ALL);
  }, [strengths, strength]);

  // Apply the active category / brand / flavor / strength filters and a
  // free-text search over name/description/brand/flavor.
  const visibleProducts = useMemo(() => {
    if (!products) return [];
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (favOnly && !favorites.has(p.id)) return false;
      if (category !== ALL && p.category !== category) return false;
      if (brand !== ALL && p.brand !== brand) return false;
      if (flavor !== ALL && p.flavor !== flavor) return false;
      if (strength !== ALL && String(p.nicotineMg) !== strength) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        (p.brand?.toLowerCase().includes(q) ?? false) ||
        (p.flavor?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [products, category, brand, flavor, strength, search, favOnly, favorites]);

  // How many filter dimensions are narrowing the catalog (badge on the button).
  const activeFilterCount =
    (category !== ALL ? 1 : 0) +
    (brand !== ALL ? 1 : 0) +
    (flavor !== ALL ? 1 : 0) +
    (strength !== ALL ? 1 : 0) +
    (favOnly ? 1 : 0);

  const clearFilters = () => {
    setCategory(ALL);
    setBrand(ALL);
    setFlavor(ALL);
    setStrength(ALL);
    if (favOnly) setFavOnly(false);
  };

  // Cross-sell picks: in-stock products not already in the cart, favouring
  // add-on categories (accessories / e-liquids). Products with variants are
  // skipped — they need a variant choice, which the strip can't offer.
  const suggestions = useMemo(() => {
    if (!products) return [];
    const addonRank = (c: string) =>
      c === "accessories" ? 0 : c === "liquids" ? 1 : 2;
    return products
      .filter(
        (p) => p.inStock && p.variants.length === 0 && !productTotals.has(p.id),
      )
      .sort((a, b) => addonRank(a.category) - addonRank(b.category))
      .slice(0, 6);
  }, [products, productTotals]);

  const handlePlaced = (placed: PublicOrder) => {
    setOrder(placed);
    setCart({});
    setPriced(null);
    setView("done");
  };

  // Reorder: merge a past order's still-available items into the cart, clamped
  // to current stock, then drop the user into the cart to review.
  const reorder = (past: PublicOrder) => {
    if (!products) return;
    const byId = new Map(products.map((p) => [p.id, p]));
    setCart((prev) => {
      const next = { ...prev };
      for (const it of past.items) {
        const p = byId.get(it.productId);
        if (!p) continue;
        const variant = it.variantId
          ? p.variants.find((v) => v.id === it.variantId)
          : undefined;
        const stock = variant ? variant.availableStock : p.availableStock;
        if (it.variantId && !variant) continue;
        if (stock <= 0) continue;
        const key = keyOf(it.productId, it.variantId);
        next[key] = Math.min((next[key] ?? 0) + it.quantity, stock);
      }
      return next;
    });
    haptic("medium");
    setView("cart");
  };

  if (loadError)
    return (
      <section className="card">
        <p className="note error">Couldn’t load catalog: {loadError}</p>
        <button className="btn secondary" onClick={loadProducts}>
          Try again
        </button>
      </section>
    );
  if (!products) return <SkeletonGrid />;

  if (view === "done" && order) {
    return (
      <Confirmation
        order={order}
        config={config}
        onDone={() => setView("catalog")}
      />
    );
  }

  if (view === "track" && order) {
    return (
      <TrackView
        order={order}
        config={config}
        onBack={() => setView("orders")}
      />
    );
  }

  if (view === "orders") {
    return (
      <OrdersView
        onBack={() => setView("catalog")}
        onReorder={reorder}
        onTrack={trackOrder}
        canReorder={!!products}
      />
    );
  }

  const preorderNote = preorderAvailable
    ? `🌙 We're closed — this will be a preorder, delivered after we open at ${config?.store.opensAt}.`
    : null;

  if (view === "account") {
    return (
      <Account
        user={user}
        onUpdated={onUserUpdate}
        onBack={() => setView("catalog")}
      />
    );
  }

  if (view === "checkout") {
    if (!priced || !priced.valid) {
      // Cart became empty/invalid (e.g. reload) — send the user back.
      return (
        <section className="card">
          <p className="gate-text">Your cart is empty.</p>
          <button className="btn secondary" onClick={() => setView("catalog")}>
            Back to catalog
          </button>
        </section>
      );
    }
    return (
      <Checkout
        items={items}
        priced={priced}
        defaultName={firstName ?? ""}
        freeVape={freeVapeReward}
        preorderNote={preorderNote}
        onPlaced={handlePlaced}
        onBack={() => setView("cart")}
      />
    );
  }

  if (view === "cart") {
    return (
      <CartView
        priced={priced}
        config={config}
        onSetQty={setQty}
        onBack={() => setView("catalog")}
        onCheckout={() => setView("checkout")}
        storeOpen={storeOpen}
        paused={paused}
        preorderAvailable={preorderAvailable}
        belowMin={belowMin}
        minOrderCents={minOrderCents}
        canCheckout={canCheckout}
        suggestions={suggestions}
      />
    );
  }

  return (
    <>
      <CatalogView
        products={visibleProducts}
        totalCount={products.length}
        greetingName={firstName ?? null}
        search={search}
        onSearch={setSearch}
        onOpenFilter={() => setFilterOpen(true)}
        activeFilterCount={activeFilterCount}
        cart={cart}
        onSetQty={setQty}
        onOpenDetail={setDetail}
        config={config}
        storeOpen={storeOpen}
        onOpenOrders={() => setView("orders")}
        onOpenAccount={() => setView("account")}
        stamps={stampSummary}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        preorderAvailable={preorderAvailable}
      />
      {filterOpen && (
        <FilterSheet
          categories={categories}
          brands={brands}
          flavors={flavors}
          strengths={strengths}
          category={category}
          brand={brand}
          flavor={flavor}
          strength={strength}
          favOnly={favOnly}
          hasFavorites={favorites.size > 0}
          resultCount={visibleProducts.length}
          onCategory={setCategory}
          onBrand={setBrand}
          onFlavor={setFlavor}
          onStrength={setStrength}
          onToggleFavOnly={() => setFavOnly((v) => !v)}
          onClear={clearFilters}
          onClose={() => setFilterOpen(false)}
        />
      )}
      {detail && (
        <ProductSheet
          product={detail}
          cart={cart}
          isFavorite={favorites.has(detail.id)}
          onToggleFavorite={toggleFavorite}
          onSetQty={setQty}
          alerts={alerts}
          onToggleAlert={onToggleAlert}
          onClose={() => setDetail(null)}
        />
      )}
    </>
  );
}

function CatalogView({
  products,
  totalCount,
  greetingName,
  search,
  onSearch,
  onOpenFilter,
  activeFilterCount,
  cart,
  onSetQty,
  onOpenDetail,
  config,
  storeOpen,
  onOpenOrders,
  onOpenAccount,
  stamps,
  favorites,
  onToggleFavorite,
  preorderAvailable,
}: {
  products: PublicProduct[];
  totalCount: number;
  greetingName: string | null;
  search: string;
  onSearch: (v: string) => void;
  onOpenFilter: () => void;
  activeFilterCount: number;
  cart: Cart;
  onSetQty: (id: string, q: number) => void;
  onOpenDetail: (p: PublicProduct) => void;
  config: StorefrontConfig | null;
  storeOpen: boolean;
  onOpenOrders: () => void;
  onOpenAccount: () => void;
  stamps: {
    count: number;
    size: number;
    freeVapeCredits: number;
    remaining: number;
  } | null;
  favorites: Set<string>;
  onToggleFavorite: (id: string) => void;
  preorderAvailable: boolean;
}) {
  if (totalCount === 0)
    return <p className="note">No products available right now.</p>;

  // Per-product cart totals (variant lines roll up onto their product).
  const totals = new Map<string, number>();
  for (const [key, q] of Object.entries(cart)) {
    const pid = key.split("::")[0]!;
    totals.set(pid, (totals.get(pid) ?? 0) + q);
  }

  return (
    <>
      {config?.store.paused && (
        <div className="closed-banner">
          ⏸ <b>We're temporarily not taking orders.</b>{" "}
          {config.store.pausedMessage || "Please check back a bit later."}
        </div>
      )}
      {!storeOpen && !config?.store.paused && config?.store && (
        <div className="closed-banner">
          🌙 <b>We're closed right now.</b> We deliver{" "}
          {config.store.opensAt}–{config.store.closesAt}.{" "}
          {preorderAvailable
            ? "You can still preorder — we'll deliver after opening."
            : "Browse now and order when we reopen."}
        </div>
      )}
      <div className="catalog-head">
        <div className="greeting">
          <span className="greeting-hi">
            Hey{greetingName ? ` ${greetingName}` : ""} 👋
          </span>
          <span className="greeting-sub">What are we vaping today?</span>
        </div>
        <div className="top-actions">
          <button
            className="pill-btn"
            onClick={onOpenOrders}
            aria-label="Your orders"
          >
            🧾 Orders
          </button>
          <button
            className="pill-btn accent"
            onClick={onOpenAccount}
            aria-label="Account"
          >
            👤
          </button>
        </div>
      </div>

      <div className="trust">
        <span className="trust-item">
          ⚡ ~{config?.deliveryEtaMinutes ?? 45} min delivery
        </span>
        <span className="trust-item">🔒 Discreet</span>
        <span className="trust-item">💳 Pay by link</span>
      </div>

      {stamps && (stamps.count > 0 || stamps.freeVapeCredits > 0) && (
        <button
          className="stampcard-strip"
          onClick={onOpenAccount}
          aria-label="Your stamp card"
        >
          <StampCard stamps={stamps} />
        </button>
      )}

      <div className="catalog-controls">
        <div className="search-row">
          <div className="search">
            <span className="search-icon">🔍</span>
            <input
              className="search-input"
              type="search"
              inputMode="search"
              placeholder="Search vapes, flavors…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
            />
            {search && (
              <button
                className="search-clear"
                aria-label="clear search"
                onClick={() => onSearch("")}
              >
                ×
              </button>
            )}
          </div>
          <button
            className={`filter-btn ${activeFilterCount > 0 ? "active" : ""}`}
            onClick={onOpenFilter}
            aria-label="Filter products"
          >
            <span className="filter-btn-icon" aria-hidden="true">
              ⚙
            </span>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {products.length === 0 ? (
        <p className="note">
          Nothing matches that. Try another search or category.
        </p>
      ) : (
        <div className="grid">
          {products.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              qty={totals.get(p.id) ?? 0}
              isFavorite={favorites.has(p.id)}
              onToggleFavorite={onToggleFavorite}
              onSetQty={onSetQty}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </div>
      )}
    </>
  );
}

const LOW_STOCK_THRESHOLD = 5;

function ProductCard({
  product,
  qty,
  isFavorite,
  onToggleFavorite,
  onSetQty,
  onOpenDetail,
}: {
  product: PublicProduct;
  qty: number;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
  onSetQty: (key: string, q: number) => void;
  onOpenDetail: (p: PublicProduct) => void;
}) {
  const soldOut = !product.inStock;
  const low = !soldOut && product.availableStock <= LOW_STOCK_THRESHOLD;
  const hasVariants = product.variants.length > 0;
  return (
    <div className="product">
      <button
        className="product-tap"
        onClick={() => onOpenDetail(product)}
        aria-label={`View ${product.name}`}
      >
        <div className="product-media">
          <img
            className="product-img"
            src={product.imageUrl}
            alt={product.name}
            loading="lazy"
            onError={handleImgError}
          />
          {soldOut && <span className="stock-badge out">Sold out</span>}
          {low && (
            <span className="stock-badge low">
              Only {product.availableStock} left
            </span>
          )}
        </div>
      </button>
      <button
        className={isFavorite ? "fav-btn on" : "fav-btn"}
        aria-label={isFavorite ? "remove favorite" : "add favorite"}
        onClick={() => {
          haptic("light");
          onToggleFavorite(product.id);
        }}
      >
        {isFavorite ? "♥" : "♡"}
      </button>
      <div className="product-body">
        <button
          className="product-name product-name-btn"
          onClick={() => onOpenDetail(product)}
        >
          {product.name}
        </button>
        {product.badge && <span className="product-badge">{product.badge}</span>}
        {product.rating != null && (
          <Stars value={product.rating} count={product.ratingCount} size={12} />
        )}
        <div className="product-desc">{product.description}</div>
        <div className="product-row">
          <span className="product-price">
            {formatMoney(product.priceCents, product.currency)}
          </span>
          {soldOut ? (
            <span className="sold-out">Out of stock</span>
          ) : hasVariants ? (
            // Variant products need a choice — the sheet handles quantities.
            <button className="add-btn" onClick={() => onOpenDetail(product)}>
              {qty > 0 ? `In cart · ${qty}` : "Choose"}
            </button>
          ) : qty === 0 ? (
            <button className="add-btn" onClick={() => onSetQty(product.id, 1)}>
              Add
            </button>
          ) : (
            <QtyStepper
              qty={qty}
              max={product.availableStock}
              onChange={(q) => onSetQty(product.id, q)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function QtyStepper({
  qty,
  max,
  onChange,
}: {
  qty: number;
  max: number;
  onChange: (q: number) => void;
}) {
  return (
    <div className="stepper">
      <button
        className="step"
        onClick={() => onChange(qty - 1)}
        aria-label="decrease"
      >
        −
      </button>
      <span className="step-qty">{qty}</span>
      <button
        className="step"
        onClick={() => onChange(qty + 1)}
        disabled={qty >= max}
        aria-label="increase"
      >
        +
      </button>
    </div>
  );
}

function CartView({
  priced,
  config,
  onSetQty,
  onBack,
  onCheckout,
  storeOpen,
  paused,
  preorderAvailable,
  belowMin,
  minOrderCents,
  canCheckout,
  suggestions,
}: {
  priced: PricedCart | null;
  config: StorefrontConfig | null;
  onSetQty: (key: string, q: number) => void;
  onBack: () => void;
  onCheckout: () => void;
  storeOpen: boolean;
  paused: boolean;
  preorderAvailable: boolean;
  belowMin: boolean;
  minOrderCents: number;
  canCheckout: boolean;
  suggestions: PublicProduct[];
}) {
  if (!priced || priced.lines.length === 0) {
    return (
      <section className="card">
        <p className="gate-text">Your cart is empty.</p>
        <button className="btn secondary" onClick={onBack}>
          Back to catalog
        </button>
      </section>
    );
  }

  return (
    <>
      <section className="card">
        {priced.lines.map((line) => {
          const key = keyOf(line.productId, line.variantId);
          return (
            <div className="cart-line" key={key}>
              <div className="cart-line-main">
                <div className="cart-line-name">
                  {line.name}
                  {line.variantName && (
                    <span className="cart-line-variant"> · {line.variantName}</span>
                  )}
                </div>
                <div className="cart-line-sub">
                  {formatMoney(line.priceCents, priced.currency)} each
                  {line.issue === "insufficient_stock" && (
                    <span className="issue">
                      {" "}
                      · only {line.availableStock} left
                    </span>
                  )}
                  {line.issue === "unavailable" && (
                    <span className="issue"> · unavailable</span>
                  )}
                </div>
              </div>
              <div className="cart-line-side">
                <QtyStepper
                  qty={line.quantity}
                  max={Math.max(line.availableStock, line.quantity)}
                  onChange={(q) => onSetQty(key, q)}
                />
                <div className="cart-line-total">
                  {formatMoney(line.lineTotalCents, priced.currency)}
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="card totals">
        <Total
          label="Subtotal"
          value={formatMoney(priced.subtotalCents, priced.currency)}
        />
        {priced.bundles.map((b) => (
          <Total
            key={b.bundleId}
            label={`🎁 ${b.name}${b.sets > 1 ? ` ×${b.sets}` : ""}`}
            value={`−${formatMoney(b.discountCents, priced.currency)}`}
          />
        ))}
        <Total
          label="Delivery"
          value={formatMoney(priced.deliveryCents, priced.currency)}
        />
        <Total
          label="Total"
          value={formatMoney(priced.totalCents, priced.currency)}
          strong
        />
      </section>

      <CartNudges priced={priced} config={config} />

      {!priced.valid ? (
        <p className="note error">
          Some items need attention before checkout (see notes above).
        </p>
      ) : paused ? (
        <p className="note error">
          ⏸ We're temporarily not taking orders
          {config?.store.pausedMessage ? ` — ${config.store.pausedMessage}` : ""}.
          Your cart is saved.
        </p>
      ) : !storeOpen && !preorderAvailable ? (
        <p className="note error">
          🌙 We're closed right now. We deliver{" "}
          {config?.store.opensAt}–{config?.store.closesAt} — your cart is saved.
        </p>
      ) : belowMin ? (
        <p className="note error">
          🛒 Minimum order is {formatMoney(minOrderCents, priced.currency)}. Add{" "}
          {formatMoney(minOrderCents - priced.subtotalCents, priced.currency)}{" "}
          more to check out.
        </p>
      ) : (
        <>
          {preorderAvailable && (
            <p className="note">
              🌙 We're closed — this will be a <b>preorder</b>, delivered after
              we open at {config?.store.opensAt}.
            </p>
          )}
          <button className="btn" onClick={onCheckout} disabled={!canCheckout}>
            Checkout · {formatMoney(priced.totalCents, priced.currency)}
          </button>
        </>
      )}

      <CrossSell products={suggestions} onSetQty={onSetQty} />

      <button className="btn secondary" onClick={onBack}>
        Back to catalog
      </button>
    </>
  );
}

/** "You might also like" — a compact add-on strip shown in the cart. */
function CrossSell({
  products,
  onSetQty,
}: {
  products: PublicProduct[];
  onSetQty: (key: string, q: number) => void;
}) {
  if (products.length === 0) return null;
  return (
    <section className="card cross-sell">
      <div className="cross-sell-title">You might also like</div>
      <div className="cross-sell-row">
        {products.map((p) => (
          <div className="cross-sell-item" key={p.id}>
            <img
              className="cross-sell-img"
              src={p.imageUrl}
              alt={p.name}
              loading="lazy"
              onError={handleImgError}
            />
            <div className="cross-sell-name">{p.name}</div>
            <div className="cross-sell-foot">
              <span className="cross-sell-price">
                {formatMoney(p.priceCents, p.currency)}
              </span>
              <button
                className="cross-sell-add"
                aria-label={`Add ${p.name}`}
                onClick={() => {
                  haptic("light");
                  onSetQty(p.id, 1);
                }}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** AOV meters: visual progress toward free delivery and toward a prize spin. */
function CartNudges({
  priced,
  config,
}: {
  priced: PricedCart;
  config: StorefrontConfig | null;
}) {
  if (!config) return null;
  const meters: ReactNode[] = [];

  if (config.freeDeliveryThresholdCents > 0) {
    const done = priced.deliveryCents === 0 && priced.subtotalCents > 0;
    const gap = config.freeDeliveryThresholdCents - priced.subtotalCents;
    meters.push(
      <Meter
        key="delivery"
        done={done}
        pct={pctOf(priced.subtotalCents, config.freeDeliveryThresholdCents)}
        label={
          done ? (
            <>🎉 Free delivery unlocked!</>
          ) : (
            <>
              Add <b>{formatMoney(gap, priced.currency)}</b> for free delivery 🛵
            </>
          )
        }
      />,
    );
  }

  const spinDone = priced.totalCents >= config.wheelMinOrderCents;
  const spinGap = config.wheelMinOrderCents - priced.totalCents;
  if (priced.totalCents > 0) {
    meters.push(
      <Meter
        key="spin"
        done={spinDone}
        pct={pctOf(priced.totalCents, config.wheelMinOrderCents)}
        label={
          spinDone ? (
            <>🎡 Prize spin unlocked — spin at checkout!</>
          ) : (
            <>
              <b>{formatMoney(spinGap, priced.currency)}</b> more unlocks a free
              prize spin 🎡
            </>
          )
        }
      />,
    );
  }

  if (meters.length === 0) return null;
  return <section className="card meters">{meters}</section>;
}

function pctOf(value: number, target: number): number {
  if (target <= 0) return 100;
  return Math.max(0, Math.min(100, (value / target) * 100));
}

function Meter({
  pct,
  label,
  done,
}: {
  pct: number;
  label: ReactNode;
  done: boolean;
}) {
  return (
    <div className="meter">
      <div className={`meter-label ${done ? "done" : ""}`}>{label}</div>
      <div className="meter-track">
        <div
          className={`meter-fill ${done ? "done" : ""}`}
          style={{ width: `${done ? 100 : pct}%` }}
        />
      </div>
    </div>
  );
}

/** Order history + one-tap reorder (CLAUDE.md §9 parity inside the Mini App). */
function OrdersView({
  onBack,
  onReorder,
  onTrack,
  canReorder,
}: {
  onBack: () => void;
  onReorder: (o: PublicOrder) => void;
  onTrack: (o: PublicOrder) => void;
  canReorder: boolean;
}) {
  const [orders, setOrders] = useState<PublicOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [cancelling, setCancelling] = useState<string | null>(null);

  const load = () => {
    setError(null);
    setOrders(null);
    listOrders()
      .then(setOrders)
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : "failed to load"),
      );
  };
  useEffect(load, []);

  const cancel = async (o: PublicOrder) => {
    setCancelling(o.id);
    try {
      const updated = await cancelOrder(o.id);
      haptic("medium");
      setOrders((prev) =>
        prev ? prev.map((x) => (x.id === updated.id ? updated : x)) : prev,
      );
    } catch {
      load(); // state changed underneath (e.g. staff accepted) — refresh
    } finally {
      setCancelling(null);
    }
  };

  if (error)
    return (
      <section className="card">
        <p className="note error">Couldn’t load orders: {error}</p>
        <button className="btn secondary" onClick={load}>
          Try again
        </button>
        <button className="btn secondary" onClick={onBack}>
          Back to catalog
        </button>
      </section>
    );
  if (!orders) return <SkeletonList rows={3} />;
  if (orders.length === 0) {
    return (
      <section className="card">
        <p className="gate-text">No orders yet. Your past orders show up here.</p>
        <button className="btn secondary" onClick={onBack}>
          Back to catalog
        </button>
      </section>
    );
  }

  return (
    <>
      <div className="account-topbar">
        <button className="back-pill" onClick={onBack}>
          <span aria-hidden="true">←</span> Back to shop
        </button>
      </div>
      {orders.map((o) => (
        <section className="card order-card" key={o.id}>
          <div className="order-head">
            <span className="order-id">
              #{o.id.slice(-8)}
              {o.preorder && <span className="order-preorder"> · 🌙 preorder</span>}
            </span>
            <span className={`order-status status-${o.status}`}>
              {o.status.replace(/_/g, " ")}
            </span>
          </div>
          <div className="order-items">
            {o.items.map((it) => (
              <div className="order-item" key={keyOf(it.productId, it.variantId)}>
                {it.quantity}× {it.name}
                {it.variantName ? ` (${it.variantName})` : ""}
              </div>
            ))}
          </div>
          <div className="order-foot">
            <span className="order-total">
              {formatMoney(o.totalCents, o.currency)}
            </span>
            <div className="order-actions">
              {o.status === "pending" && (
                <button
                  className="track-btn"
                  disabled={cancelling === o.id}
                  onClick={() => cancel(o)}
                >
                  {cancelling === o.id ? "…" : "Cancel"}
                </button>
              )}
              {isActiveOrder(o.status) && (
                <button className="track-btn" onClick={() => onTrack(o)}>
                  Track
                </button>
              )}
              <button
                className="reorder-btn"
                disabled={!canReorder}
                onClick={() => onReorder(o)}
              >
                Reorder
              </button>
            </div>
          </div>
        </section>
      ))}
      <button className="btn secondary" onClick={onBack}>
        Back to catalog
      </button>
    </>
  );
}

/** Loading placeholders — better perceived performance than a bare text line. */
function SkeletonList({ rows }: { rows: number }) {
  return (
    <div className="skeleton-list">
      {Array.from({ length: rows }).map((_, i) => (
        <div className="skeleton-card" key={i} />
      ))}
    </div>
  );
}

/** Catalog-shaped skeleton shown while the product grid loads. */
function SkeletonGrid() {
  return (
    <div className="grid" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div className="skeleton-product" key={i}>
          <div className="skeleton-img" />
          <div className="skeleton-line" />
          <div className="skeleton-line short" />
        </div>
      ))}
    </div>
  );
}

/** Standalone live-tracking screen, opened from order history. */
function TrackView({
  order,
  config,
  onBack,
}: {
  order: PublicOrder;
  config: StorefrontConfig | null;
  onBack: () => void;
}) {
  return (
    <>
      <OrderTracker order={order} config={config} />

      <section className="card">
        {order.items.map((it) => (
          <div className="row" key={keyOf(it.productId, it.variantId)}>
            <span className="row-label">
              {it.quantity}× {it.name}
              {it.variantName ? ` (${it.variantName})` : ""}
            </span>
            <span className="row-value">
              {formatMoney(it.priceCents * it.quantity, order.currency)}
            </span>
          </div>
        ))}
        <div className="row row-strong">
          <span className="row-label">Total (pay by link)</span>
          <span className="row-value">
            {formatMoney(order.totalCents, order.currency)}
          </span>
        </div>
      </section>

      <section className="card">
        <Total label="Deliver to" value={order.delivery.name} />
        <Total label="Address" value={order.delivery.address} />
      </section>

      <button className="btn secondary" onClick={onBack}>
        Back to orders
      </button>
    </>
  );
}

function Confirmation({
  order,
  config,
  onDone,
}: {
  order: PublicOrder;
  config: StorefrontConfig | null;
  onDone: () => void;
}) {
  return (
    <>
      <section className="card confirm">
        <div className="confirm-check">✅</div>
        <h2 className="gate-title">
          {order.preorder ? "Preorder placed" : "Order placed"}
        </h2>
        <p className="gate-text">
          Order <strong>#{order.id.slice(-8)}</strong> is in. Check the bot chat
          for <strong>payment details</strong> — pay to confirm your order.
          {order.preorder &&
            " 🌙 We're currently closed — your order will be delivered after we open."}
        </p>
      </section>

      <OrderTracker order={order} config={config} />

      {order.spin.eligible && (
        <Wheel
          orderId={order.id}
          alreadyWonLabel={order.spin.spun ? order.spin.prizeLabel : null}
        />
      )}

      <section className="card">
        {order.items.map((it) => (
          <div className="row" key={keyOf(it.productId, it.variantId)}>
            <span className="row-label">
              {it.quantity}× {it.name}
              {it.variantName ? ` (${it.variantName})` : ""}
            </span>
            <span className="row-value">
              {formatMoney(it.priceCents * it.quantity, order.currency)}
            </span>
          </div>
        ))}
        <div className="row row-strong">
          <span className="row-label">Total</span>
          <span className="row-value">
            {formatMoney(order.totalCents, order.currency)}
          </span>
        </div>
      </section>

      <section className="card">
        <Total label="Deliver to" value={order.delivery.name} />
        <Total label="Address" value={order.delivery.address} />
      </section>

      <button className="btn" onClick={onDone}>
        Back to shop
      </button>
    </>
  );
}

function Total({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className={`row ${strong ? "row-strong" : ""}`}>
      <span className="row-label">{label}</span>
      <span className="row-value">{value}</span>
    </div>
  );
}
